import {
  useAkaAdviceBaseAmountSelector,
  useAkaAdviceNetIncomeSelector,
} from "Store/akaAdvice/akaAdviceSelectors";
import React, { useState } from "react";
import {
  getDisabilityMonthlyChartData,
  getDisabilityMonthlyChartLayout,
} from "./akaAdviceChartDisabilityMonthlyConfig";
import {
  getDisabilityYearlyChartData,
  getDisabilityYearlyChartLayout,
} from "./akaAdviceChartDisabilityYearlyConfig";

import { PaddedSurface as Surface } from "App/global-style";
import { BarChart } from "Components/Chart/BarChart";
import { ChartDivId } from "Components/Chart/chartInterface";
import { ThemedButton } from "Components/Themes/components/ThemedButton";
import { AdviceProductDetailsFormValues } from "Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { Description } from "Features/CheckPrice/Products/Advice/adviceStyled";
import { CoverageNeed } from "Store/akaAdvice/interfaces/CollectiveAdvice";
import { useUserIdentificationSelector } from "Store/userIdentification/userIdentificationSelectors";
import { buttonTrackId } from "Utils/analytics";
import { useWindowSize } from "Utils/screenSizeUtils";
import { FormikProps } from "formik";
import { Collapse } from "react-collapse";
import { AkaDisabilityAdviceSummary } from "../../DisabilityAdviceCard/AkaDisabilityAdviceSummary";
import { AkaAdvicePriceFormValues } from "../../interfaces";

interface Props {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  adviceDetails: AdviceProductDetailsFormValues;
}

export const AkaAdviceChartDisability = ({
  formProps,
  need,
  adviceDetails,
}: Props): React.ReactElement => {
  const [showDetails, toggleDetails] = useState(false);
  const handleToggleDetails = (): void => {
    toggleDetails(!showDetails);
  };

  const windowSize = useWindowSize();
  const userIdentification = useUserIdentificationSelector();
  const baseAmount = useAkaAdviceBaseAmountSelector();
  const netIncome = useAkaAdviceNetIncomeSelector();

  const monthlyChartData = getDisabilityMonthlyChartData(
    formProps,
    need,
    baseAmount,
  );
  const yearlyChartData = getDisabilityYearlyChartData(
    formProps,
    need,
    adviceDetails,
    baseAmount,
    userIdentification?.newCustomerResponse?.dateOfBirth,
  );

  return (
    <>
      <div className={ChartDivId.DISABILITY_MONTHLY}>
        <Description fw={500} size="16px" className="AdviceChartTitle">
          Slik blir din månedlige inntekt hvis du blir ufør*
        </Description>
        <AkaDisabilityAdviceSummary formProps={formProps} need={need} />
        <BarChart
          divId={ChartDivId.DISABILITY_MONTHLY}
          data={monthlyChartData}
          layout={getDisabilityMonthlyChartLayout(windowSize, need, netIncome)}
          isStackedBarChart
        />
        <Surface>
          <Description fw={400} size="14px" className="AdviceChartFootnotes">
            *A+ Gruppeliv uførekapital (engangsutbetaling) kan komme i tillegg,
            hvis NAV definerer deg som varig ufør, eller når du har vært minst
            40 prosent arbeidsufør sammenhengende i fire år - uansett årsak.
          </Description>
          <Description fw={400} size="14px" className="AdviceChartFootnotes">
            **Beløpene er estimat etter skatt og vil kunne avvike blant annet
            som følge av forskjellige regler for regulering.
          </Description>
        </Surface>
      </div>
      <ThemedButton
        type="button"
        id="advice__disability-payments-per-year-button"
        trackId={buttonTrackId("See payments per year")}
        iconRight={showDetails ? "chevron-up" : "chevron-down"}
        variant="text"
        onClick={handleToggleDetails}
      >
        {"Se utbetalinger per år inkl. uførekapital"}
      </ThemedButton>
      <Collapse isOpened={showDetails}>
        <div className={ChartDivId.DISABILITY_YEARLY}>
          <BarChart
            divId={ChartDivId.DISABILITY_YEARLY}
            data={yearlyChartData}
            layout={getDisabilityYearlyChartLayout(windowSize, need, netIncome)}
            isStackedBarChart
          />
        </div>
      </Collapse>
    </>
  );
};
